import { C } from "@bd/content";
import { moraleTarget, money, palletCapacity, palletsUsed, priceIndex, roomFx, storageCost, teamMood, type Company, type GameState, type Staff, fairPay, comfort, veteran, perkFx } from "@bd/sim";
import type { WhyLine } from "@bd/ui-kit";
import { t } from "../i18n";

export const fmtMoney = (v: number) => money(v);
export const fmtSigned = (v: number) => (v >= 0 ? "+" : "") + money(v);
export const pct = (v: number) => `${Math.round(v * 100)}%`;
export const k = (v: number) => (Math.abs(v) >= 1e6 ? `${(v / 1e6).toFixed(1)}M` : Math.abs(v) >= 1e4 ? `${(v / 1e3).toFixed(1)}K` : Math.round(v).toLocaleString("en-US"));

const CAT: Record<string, string> = { sales: "Game sales", salaries: "Salaries", rent: "Rent", storage: "Storage", loans: "Loan payments", cafe: "Game café", stores: "Your other shops", overheads: "Managers, insurance and IT", tax: "Tax on last year's profit", rd: "R&D", royalties: "Royalties", investor: "Investor's share" };

/** Last week's money in and out, line by line. Lines add up to the weekly change. */
export function weeklyCashLines(co: Company): { lines: WhyLine[]; total: number } {
  const w = co.ledger[co.ledger.length - 1];
  if (!w) return { lines: [{ label: t("No sales yet"), value: 0 }], total: 0 };
  const lines = Object.entries(w.byCat).filter(([, v]) => Math.abs(v) >= 0.5).map(([key, v]) => ({ label: t(CAT[key] ?? key), value: Math.round(v) }));
  return { lines, total: lines.reduce((s, l) => s + (l.value as number), 0) };
}

export function weeklyNet(co: Company): number {
  const recent = co.ledger.slice(-4);
  return recent.length ? recent.reduce((s, w) => s + w.income - w.costs, 0) / recent.length : 0;
}

export function moraleLines(co: Company, s: Staff, recentHits: number, week = 0): WhyLine[] {
  const mood = teamMood(co, week);
  const fair = fairPay(s, mood.idx);
  const lines: WhyLine[] = [{ label: t("Starting point"), value: 58 }];
  if (!s.founder) lines.push({ label: t("Pay"), value: Math.round(Math.max(-15, Math.min(10, (s.salary / fair - 1) * 40))) });
  const comf = comfort(co, s, mood);
  if (comf) lines.push({ label: comf >= 18 ? t("Rooms, decor, pets (max 18)") : t("Rooms, decor, pets"), value: Math.round(comf) });
  if (veteran(s)) lines.push({ label: t("Seen it all (level 11+)"), value: -veteran(s) });
  const perks = Math.min(10, perkFx(co, "morale"));
  if (perks) lines.push({ label: perks >= 10 ? t("Research perks (max 10)") : t("Research perks"), value: Math.round(perks) });
  if (recentHits) lines.push({ label: t("Recent hits"), value: Math.min(8, recentHits * 3) });
  if ((s.idleWeeks ?? 0) > 6) lines.push({ label: t("Nothing to work on"), value: -Math.min(8, (s.idleWeeks ?? 0) - 6) });
  if (mood.yearsSinceHit > 3) lines.push({ label: t("No hit for a while"), value: -Math.round(Math.min(8, (mood.yearsSinceHit - 3) * 2)) });
  if (mood.crowded) lines.push({ label: t("Crowded, no break room"), value: -5 });
  if (s.crunch) lines.push({ label: t("Crunch"), value: -14 });
  if (co.cash < 0) lines.push({ label: t("Money worries"), value: -8 });
  const target = Math.round(moraleTarget(co, s, recentHits, mood));
  const sum = lines.reduce((a, l) => a + (l.value as number), 0);
  if (target !== sum) lines.push({ label: t("Personality and team"), value: target - sum });
  return lines;
}

export function teamMorale(co: Company): number {
  return co.staff.length ? co.staff.reduce((a, s) => a + s.morale, 0) / co.staff.length : 0;
}

export function storageLines(state: GameState, co: Company): WhyLine[] {
  const st = storageCost(state, co);
  return [
    { label: t("Pallets of stock"), value: palletsUsed(co) },
    { label: t("Space for pallets"), value: palletCapacity(co), note: t("Warehouse rooms add space") },
    { label: t("Stored outside"), value: st.over },
    { label: t("Weekly cost"), value: money(st.cost) },
  ];
}

export function researchLines(co: Company): WhyLine[] {
  const designers = co.staff.filter((s) => s.role === "designer").reduce((a, s) => a + 0.015 * (s.stats.creativity / 50), 0);
  return [
    { label: t("Each release"), value: "3–10", note: t("More for a new combo or great reviews") },
    { label: t("Designers"), value: t("{n} a year", { n: (designers * 52).toFixed(1) }) },
    { label: t("You have"), value: Math.floor(co.research) },
  ];
}

export function inflationNote(week: number): string {
  return t("Prices rise every year ({pct}% since 1965).", { pct: Math.round((priceIndex(week) - 1) * 100) });
}

export function roomStats(co: Company, roomId: string) {
  const r = co.rooms.find((x) => x.id === roomId)!;
  const t = C.room[r.type];
  const seats = r.furniture.filter((f) => C.furniture[f.kind]?.seat).length;
  const light = r.furniture.reduce((a, f) => a + (C.furniture[f.kind]?.fx.light ?? 0), 0);
  const comfort = Math.round(40 + Math.min(60, r.furniture.length * 6 + r.furniture.filter((f) => C.furniture[f.kind]?.plant).length * 6));
  return { room: r, type: t, seats, light: Math.min(100, 30 + light * 4), comfort, effect: roomFx({ ...co, rooms: [r] } as Company, Object.keys(t.fx)[0]) };
}
